import { useState } from 'react';
import { 
  Search, 
  PackageSearch, 
  ShieldCheck, 
  Zap, 
  CheckCircle2, 
  Layers, 
  ArrowRight 
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { SHOP_CATEGORIES } from '../../../lib/shop-taxonomy.js';

export function PortalSearchHero({
  tenant,
  catalogCount = null,
  onSearch,
  onSelectCategory,
  onNavigate,
  heroTitle,
  heroSubtitle,
  heroCtaPrimary,
  heroCtaSecondary,
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const countValue = typeof catalogCount === 'number' && catalogCount > 0 ? catalogCount : null;
  const formattedCount = countValue
    ? `${countValue.toLocaleString('de-DE')} Fachartikel`
    : 'Fachkatalog';

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchTerm.trim();
    if (onSearch) onSearch(query);
    if (onNavigate && query) {
      onNavigate(`/catalog?search=${encodeURIComponent(query)}`);
    } else if (onNavigate) {
      onNavigate('/catalog');
    }
  };

  const handleCategoryClick = (catSlug) => {
    if (onSelectCategory) onSelectCategory(catSlug);
    if (onNavigate) onNavigate(`/catalog/${catSlug}`);
  };

  const handleNav = (path) => {
    if (onNavigate) onNavigate(path);
  };

  const categoryShortcuts = [
    { slug: 'brandmeldetechnik', label: 'Brandmelder' },
    { slug: 'videoueberwachung', label: 'IP-Kameras' },
    { slug: 'stromversorgung-infrastruktur', label: 'Netzteile & USV' },
    { slug: 'zutrittskontrolle', label: 'Zutrittskontrolle' },
    { slug: 'alarmtechnik', label: 'Alarmtechnik' },
  ];

  return (
    <section
      data-hero-variant="portal_search"
      className="relative overflow-hidden rounded-3xl border border-[var(--border)] bg-white p-5 sm:p-10 lg:p-14 shadow-sm"
    >
      <div className="relative z-10 mx-auto max-w-4xl text-center space-y-8">
        
        {/* Verified Catalog Badge */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          <span
            data-testid="catalog-count-badge"
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--primary,#e3000b)]/10 px-3.5 py-1 text-xs font-bold text-[var(--primary,#e3000b)]"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>{formattedCount}</span>
          </span>
          <span className="inline-flex items-center rounded-full border border-[var(--border)] bg-white px-3 py-1 text-xs font-medium text-[var(--secondary)]">
            Fachhandelsportal
          </span>
        </div>

        {/* Headline */}
        <div className="space-y-3">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-5xl lg:text-6xl">
            {heroTitle ? (
              heroTitle
            ) : (
              <>
                Sicherheitstechnik <span className="text-[var(--primary)]">direkt finden</span> &amp; disponieren.
              </>
            )}
          </h1>
          <p className="mx-auto max-w-2xl text-sm sm:text-base leading-relaxed text-[var(--secondary)]">
            {heroSubtitle
              ? heroSubtitle
              : `Gezielte SKU-, Hersteller- und Artikelsuche im ${tenant?.name || 'ASL Partner-Shop'} Sortiment für Errichter und Fachplaner.`}
          </p>
        </div>

        {/* Prominent Center Search Bar */}
        <form
          onSubmit={handleSearchSubmit}
          className="mx-auto max-w-2xl rounded-2xl border-2 border-[var(--primary)]/30 bg-white p-2 shadow-lg ring-4 ring-[var(--primary)]/5 transition-all focus-within:border-[var(--primary)] focus-within:ring-[var(--primary)]/20"
        >
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-muted-foreground ml-3 shrink-0" />
            <input
              type="search"
              placeholder="Artikelnummer (z. B. 7500311, 1060883) oder Suchbegriff..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent px-2 py-2 text-sm text-[var(--foreground)] placeholder:text-muted-foreground focus:outline-none"
              autoFocus={false}
            />
            <Button
              type="submit"
              size="lg"
              className="h-11 px-6 font-bold shrink-0 cursor-pointer"
            >
              <span>Suchen</span>
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </form>

        {/* Quick Action CTAs */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center pt-2">
          <Button
            size="lg"
            className="h-12 px-6 text-sm sm:text-base font-bold shadow-sm cursor-pointer"
            onClick={() => handleNav('/catalog')}
          >
            {heroCtaPrimary || <span>Gesamten Katalog durchsuchen</span>}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-12 px-6 text-sm sm:text-base font-medium cursor-pointer"
            onClick={() => handleNav('/cart')}
          >
            {heroCtaSecondary || <span>Projekt anfragen</span>}
          </Button>
        </div>

        {/* Category Shortcut Pills */}
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)]">
            Schnellzugriff nach Systembereich:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categoryShortcuts.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => handleCategoryClick(cat.slug)}
                className="rounded-full border border-[var(--border)] bg-white px-3.5 py-1.5 text-xs font-semibold text-[var(--foreground)] hover:border-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--surface-container-low)] transition-colors cursor-pointer shadow-2xs"
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Trust Badges Footer */}
        <div className="pt-6 border-t border-[var(--border)] grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-semibold text-[var(--secondary)]">
          <div className="flex items-center justify-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--primary)] shrink-0" />
            <span>Fachsortiment</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--primary)] shrink-0" />
            <span>Staffelpreise & Projektanfragen</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--primary)] shrink-0" />
            <span>Persönliche Beratung & Projektlösungen</span>
          </div>
        </div>

      </div>
    </section>
  );
}
